import { Option } from '@sapphire/result';

import { APP_ROUTES } from '@ps/core-config';
import { isDraftBlockedForOperator, Survey } from '@ps/survey-utils';

import { routableSurveyId } from '../../state/core/utils/survey-route.helper';

export interface SurveyRedirectInput {
  readonly routeSurveyId: number;
  readonly survey: Survey;
  readonly isOperatorWithoutBuyer: boolean;
  readonly routeFor: (surveyId: number) => string;
}

/**
 * A child's own id is not a page of its own: the page belongs to the project.
 * A project's own id is not affected, because its active child has a different id than the route's.
 */
function projectOfChildOwnId({
  routeSurveyId,
  survey,
  routeFor,
}: SurveyRedirectInput): Option<string> {
  const routableId = routableSurveyId(survey);
  return survey.id === routeSurveyId && routableId !== routeSurveyId
    ? Option.some(routeFor(routableId))
    : Option.none;
}

/**
 * Where a loaded survey's page should send the user instead, if any.
 *
 * @see `isDraftBlockedForOperator` for details on the off-limits rule.
 */
function dashboardForOffLimitsDraft({
  survey,
  isOperatorWithoutBuyer,
}: SurveyRedirectInput): Option<string> {
  return isDraftBlockedForOperator(survey, isOperatorWithoutBuyer)
    ? Option.some(APP_ROUTES.DASHBOARD)
    : Option.none;
}

/** Where a loaded survey's page should send the user instead, if anywhere. */
export function surveyRedirectRoute(
  input: SurveyRedirectInput,
): Option<string> {
  return projectOfChildOwnId(input).orElse(() =>
    dashboardForOffLimitsDraft(input),
  );
}
